import React, { ReactNode, useState } from 'react';
import {
  Autocomplete as MuiAutocomplete,
  CircularProgress,
} from '@mui/material';
import { Controller, get, useFormContext } from 'react-hook-form';
import {
  AutocompleteInput,
  AutocompleteItem,
} from 'src/components/Forms/FormInputs/styled';

interface OptionType {
  label: string;
  key: string;
}

interface AutocompleteProps<T extends OptionType, K> {
  name: string;
  options: T[];
  placeholder: string;
  optionIcon?: ReactNode;
  loading?: boolean;
  onManualChange?: (key: string) => void;
  isOptionEqualToValue?: (option: T, value: K) => boolean;
  getOptionLabel?: (option: T) => string;
}

export const Autocomplete = <T extends OptionType, K>({
  name,
  options,
  optionIcon,
  placeholder,
  loading,
  onManualChange,
  isOptionEqualToValue,
  getOptionLabel,
}: AutocompleteProps<T, K>): JSX.Element => {
  const [open, setOpen] = useState(false);

  const {
    control,
    formState: { errors },
  } = useFormContext();

  const error = get(errors, name);

  const wrappedIsOptionEqualToValue = isOptionEqualToValue
    ? (option: T, value: T) =>
        isOptionEqualToValue(option, value as unknown as K)
    : undefined;

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <Controller
      control={control}
      name={name}
      render={({ field: { onChange, onBlur, value } }) => (
        <MuiAutocomplete
          disableClearable
          disabled={loading}
          loading={loading}
          open={open}
          onOpen={handleOpen}
          onClose={handleClose}
          onBlur={onBlur}
          onChange={(_, newValue) => {
            onManualChange
              ? onManualChange(newValue.key)
              : onChange(newValue.key);
          }}
          options={options}
          value={value || null}
          isOptionEqualToValue={wrappedIsOptionEqualToValue}
          getOptionLabel={getOptionLabel}
          renderOption={(props, option) => (
            <AutocompleteItem {...props}>
              {optionIcon}
              {option.label}
            </AutocompleteItem>
          )}
          renderInput={(params) => (
            <AutocompleteInput
              {...params}
              error={!!error?.message}
              open={open}
              placeholder={open ? 'Search...' : placeholder}
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loading && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      )}
    />
  );
};
